import { useState, useRef, useCallback } from 'react';
import type { SoapNote } from '@/lib/stores/intelligence-store';
import { useIntelligenceStore } from '@/lib/stores/intelligence-store';
import { useLcvfrSoap, useLcvfrPipelineActive } from '@/lib/hooks/useLcvfrArtifact';
import { PanelSkeleton } from '@/components/ui/SkeletonLoader';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

type SoapKey = 'S' | 'O' | 'A' | 'P';

interface SoapPanelProps {
  soap: SoapNote | null;
  loading?: boolean;
}

interface SoapSectionProps {
  label: string;
  abbreviation: SoapKey;
  content: string;
  color: string;
  dotColor: string;
  borderColor: string;
  isEditing: boolean;
  isModified: boolean;
  onStartEdit: () => void;
  onSave: (value: string) => void;
  onCancel: () => void;
  staggerClass: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// SOAP Section Component (with inline editing)
// ─────────────────────────────────────────────────────────────────────────────

function SoapSection({
  label,
  abbreviation,
  content,
  color,
  dotColor,
  borderColor,
  isEditing,
  isModified,
  onStartEdit,
  onSave,
  onCancel,
  staggerClass,
}: SoapSectionProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [editValue, setEditValue] = useState(content);

  const handleStartEdit = useCallback(() => {
    setEditValue(content);
    onStartEdit();
    // Focus the textarea after render
    requestAnimationFrame(() => {
      textareaRef.current?.focus();
      // Move cursor to end
      if (textareaRef.current) {
        textareaRef.current.selectionStart = textareaRef.current.value.length;
        textareaRef.current.selectionEnd = textareaRef.current.value.length;
      }
    });
  }, [content, onStartEdit]);

  const handleBlur = useCallback(() => {
    if (editValue !== content) {
      onSave(editValue);
    } else {
      onCancel();
    }
  }, [editValue, content, onSave, onCancel]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEditValue(content);
        onCancel();
      }
      // Ctrl/Cmd+Enter to save
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        onSave(editValue);
      }
    },
    [content, editValue, onSave, onCancel],
  );

  return (
    <div className={`group animate-fade-in ${staggerClass}`}>
      <div
        className={`rounded-lg border ${borderColor} bg-white transition-all duration-300 hover:shadow-sm`}
        style={{ borderLeftWidth: '3px' }}
      >
        {/* Section header */}
        <div className="flex items-center gap-2.5 px-4 pt-3 pb-1">
          {/* Abbreviation badge */}
          <span
            className={`flex h-7 w-7 items-center justify-center rounded-md text-xs font-bold ${color}`}
          >
            {abbreviation}
          </span>

          {/* Label as pill badge with colored dot */}
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">
            <span className={`inline-block h-1.5 w-1.5 rounded-full ${dotColor}`} />
            {label}
          </span>

          {/* Modified indicator */}
          {isModified && (
            <span
              className="ml-auto flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium tabular-nums text-amber-600 transition-opacity duration-200"
              title="Section has been edited"
            >
              <svg
                className="h-3 w-3"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z"
                />
              </svg>
              edited
            </span>
          )}

          {/* Edit button (visible on hover when not editing) */}
          {!isEditing && (
            <button
              onClick={handleStartEdit}
              className={`rounded-md p-1.5 text-slate-400 opacity-0 transition-all duration-150 hover:bg-slate-100 hover:text-slate-600 focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-teal-500/30 group-hover:opacity-100 ${
                isModified ? 'ml-1' : 'ml-auto'
              }`}
              title={`Edit ${label}`}
              aria-label={`Edit ${label} section`}
            >
              <svg
                className="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Content area */}
        {isEditing ? (
          <div className="animate-fade-in px-4 pb-3 pt-1">
            <textarea
              ref={textareaRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              rows={Math.max(3, editValue.split('\n').length + 1)}
              className="w-full resize-y rounded-md border border-teal-300 bg-white px-3 py-2 text-sm leading-relaxed text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500/20"
              placeholder={`Enter ${label.toLowerCase()} notes...`}
              aria-label={`${label} section editor`}
            />
            <div className="mt-1.5 flex items-center justify-between">
              <p className="text-[11px] text-slate-400">
                Ctrl+Enter to save, Esc to cancel
              </p>
              <div className="flex gap-1.5">
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    setEditValue(content);
                    onCancel();
                  }}
                  className="rounded-md px-2.5 py-1 text-xs font-medium text-slate-500 transition-colors duration-150 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                >
                  Cancel
                </button>
                <button
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onSave(editValue);
                  }}
                  className="rounded-md bg-teal-50 px-2.5 py-1 text-xs font-medium text-teal-700 transition-colors duration-150 hover:bg-teal-100 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div
            onClick={handleStartEdit}
            className="cursor-text rounded-b-lg px-4 pb-3 pt-1 transition-all duration-200 hover:bg-slate-50/60"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleStartEdit();
              }
            }}
            aria-label={`Click to edit ${label}`}
          >
            <p className="text-sm leading-relaxed text-slate-700">
              {content || (
                <span className="italic text-slate-400">
                  No data -- click to add
                </span>
              )}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SOAP Panel
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SOAP note display panel with inline editing.
 * Shows Subjective, Objective, Assessment, and Plan sections.
 * Click to edit each section; auto-saves on blur.
 */
export function SoapPanel({ soap, loading }: SoapPanelProps): JSX.Element {
  const setSoap = useIntelligenceStore((s) => s.setSoap);
  const [editingSection, setEditingSection] = useState<SoapKey | null>(null);
  const [modifiedSections, setModifiedSections] = useState<Set<SoapKey>>(new Set());

  // LCVFR artifact subscription — prefer over legacy Feed E
  const lcvfr = useLcvfrSoap();
  const pipelineActive = useLcvfrPipelineActive();

  if (loading) {
    return <PanelSkeleton />;
  }

  // Dual-path: LCVFR artifacts take priority over legacy Feed E SOAP
  const effectiveSoap: SoapNote | null = lcvfr.soap
    ? {
        S: lcvfr.soap.subjective,
        O: lcvfr.soap.objective,
        A: lcvfr.soap.assessment,
        P: lcvfr.soap.plan,
        version: lcvfr.sequence,
      }
    : soap;

  const handleSave = (key: SoapKey, value: string) => {
    if (!effectiveSoap) return;

    const updatedSoap: SoapNote = {
      ...effectiveSoap,
      [key]: value,
      version: effectiveSoap.version,
    };
    setSoap(updatedSoap);

    // Track that this section was modified
    setModifiedSections((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });

    setEditingSection(null);
  };

  const handleCancel = () => {
    setEditingSection(null);
  };

  const sections: Array<{
    key: SoapKey;
    label: string;
    color: string;
    dotColor: string;
    borderColor: string;
    stagger: string;
  }> = [
    {
      key: 'S',
      label: 'Subjective',
      color: 'bg-blue-50 text-blue-700',
      dotColor: 'bg-blue-500',
      borderColor: 'border-blue-200 border-l-blue-400',
      stagger: 'animate-stagger-1',
    },
    {
      key: 'O',
      label: 'Objective',
      color: 'bg-emerald-50 text-emerald-700',
      dotColor: 'bg-emerald-500',
      borderColor: 'border-emerald-200 border-l-emerald-400',
      stagger: 'animate-stagger-2',
    },
    {
      key: 'A',
      label: 'Assessment',
      color: 'bg-amber-50 text-amber-700',
      dotColor: 'bg-amber-500',
      borderColor: 'border-amber-200 border-l-amber-400',
      stagger: 'animate-stagger-3',
    },
    {
      key: 'P',
      label: 'Plan',
      color: 'bg-purple-50 text-purple-700',
      dotColor: 'bg-purple-500',
      borderColor: 'border-purple-200 border-l-purple-400',
      stagger: 'animate-stagger-4',
    },
  ];

  return (
    <div className="card animate-fade-in overflow-hidden">
      {/* ── Header ── */}
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
        <div className="flex items-center gap-3">
          <h3 className="section-header">SOAP Note</h3>

          {/* LCVFR path indicator badge */}
          {lcvfr.source && (
            <span
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                lcvfr.source === 'final'
                  ? 'badge-teal'
                  : 'bg-cyan-50 text-cyan-700'
              }`}
            >
              <span
                className={`inline-block h-1.5 w-1.5 rounded-full ${
                  lcvfr.source === 'final' ? 'bg-teal-500' : 'bg-cyan-500 animate-pulse'
                }`}
              />
              {lcvfr.source === 'final' ? 'artifact' : 'live'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {/* Pipeline active indicator */}
          {pipelineActive && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-cyan-50 px-2.5 py-0.5 text-[10px] font-medium text-cyan-600">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-500 animate-pulse" />
              processing
            </span>
          )}

          {/* Modified sections counter */}
          {modifiedSections.size > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium tabular-nums text-amber-600">
              <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487z" />
              </svg>
              {modifiedSections.size} edited
            </span>
          )}

          {/* Version pill */}
          {effectiveSoap && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-medium tabular-nums text-slate-500">
              v{effectiveSoap.version}
            </span>
          )}
        </div>
      </div>

      {/* ── Body ── */}
      {effectiveSoap ? (
        <div className="space-y-3 p-5">
          {sections.map((section) => (
            <SoapSection
              key={section.key}
              label={section.label}
              abbreviation={section.key}
              content={effectiveSoap[section.key]}
              color={section.color}
              dotColor={section.dotColor}
              borderColor={section.borderColor}
              isEditing={editingSection === section.key}
              isModified={modifiedSections.has(section.key)}
              onStartEdit={() => setEditingSection(section.key)}
              onSave={(value) => handleSave(section.key, value)}
              onCancel={handleCancel}
              staggerClass={section.stagger}
            />
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-state-icon animate-breathe">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          </div>
          <p className="empty-state-title">No SOAP note available</p>
          <p className="empty-state-desc">Start recording to generate.</p>
        </div>
      )}
    </div>
  );
}
